import { Component, ErrorInfo, ReactNode } from 'react'
import { store } from '@/store/store'
import { showSnackbar } from '@/store/slices/uiSlice'
import { copyToClipboard } from '@/utils/clipboardUtils'

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error?: Error
    errorInfo?: ErrorInfo
    copied?: boolean
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({
            error,
            errorInfo,
        })

        // Log error to console in development
        if (import.meta.env.DEV) {
            console.error('ErrorBoundary caught an error:', error, errorInfo)
        }

        // Show snackbar notification
        store.dispatch(showSnackbar({ message: 'Something went wrong. Please refresh the page and try again.', type: 'error' }))

        // TODO: Send error to logging service (Sentry, LogRocket, etc.)
        // errorReportingService.captureException(error, { extra: errorInfo })
    }

    private handleReload = () => {
        window.location.reload()
    }

    private handleGoHome = () => {
        window.location.href = '/'
    }

    private handleCopyError = async () => {
        if (!this.state.error) return

        const errorText = `Error: ${this.state.error.message}\n\nStack Trace:\n${this.state.error.stack || 'No stack trace available'}`
        
        const success = await copyToClipboard(errorText)
        
        if (success) {
            this.setState({ copied: true })
            setTimeout(() => {
                this.setState({ copied: false })
            }, 2000)
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 mx-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full mx-auto mb-4">
                            <svg
                                className="w-6 h-6 text-red-600 dark:text-red-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                            </svg>
                        </div>

                        <div className="text-center">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Oops! Something went wrong
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                                We encountered an unexpected error. This has been logged and we're working to fix it.
                            </p>

                            {import.meta.env.DEV && this.state.error && (
                                <div className="text-left bg-gray-50 dark:bg-gray-700 p-3 rounded-md mb-4 text-xs">
                                    <div className="flex items-center justify-between mb-2">
                                        <details className="flex-1">
                                            <summary className="cursor-pointer text-gray-700 dark:text-gray-300 font-medium">
                                                Error Details (Development)
                                            </summary>
                                            <div className="mt-2 space-y-2">
                                                <div>
                                                    <strong className="text-red-600 dark:text-red-400">Error:</strong>
                                                    <pre className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                                                        {this.state.error.message}
                                                    </pre>
                                                </div>
                                                {this.state.error.stack && (
                                                    <div>
                                                        <strong className="text-red-600 dark:text-red-400">Stack Trace:</strong>
                                                        <pre className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap text-xs overflow-x-auto">
                                                            {this.state.error.stack}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        </details>
                                        <button
                                            onClick={this.handleCopyError}
                                            className="ml-2 px-2 py-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded text-xs font-medium transition-colors whitespace-nowrap"
                                            title="Copy error details to clipboard"
                                        >
                                            {this.state.copied ? '✓ Copied' : 'Copy'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={this.handleReload}
                                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-200"
                                >
                                    Reload Page
                                </button>
                                <button
                                    onClick={this.handleGoHome}
                                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors duration-200"
                                >
                                    Go Home
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary